/* ================================================================
   图纸渲染 · 把单页图纸画到 2D Canvas
   参考 local-clone 的 crate2DTool.Crate2D 实现思路：
     1. 离屏 canvas 当画布
     2. 画外边框、标题、3D 截图、标题栏、说明、水印
     3. canvas → JPEG dataURL → jspdf.addImage
   v1 链路打通：第一页画 3D 截图，2-5 页用占位文字
   ================================================================ */

import { jsPDF } from 'jspdf';
import type { RenderNode, SideId, BomLine, Material } from '../core/model/types';

/** 局部材料行：按当前页 sideIds 过滤 renderNodes 后重新聚合（避免直接用全量 BomLine 跨面合并的 count 误导） */
interface PageBomRow {
  spec: string;
  count: number;
  materialName: string;
  totalAreaM2: number;
  totalVolumeM3: number;
  totalWeightKg: number;
  fromSides: SideId[];
}

/** 按 sideIds 过滤 renderNodes，再按 (material, spec) 聚合出当前页的材料表 */
function summarizePageBom(
  nodes: RenderNode[],
  sideIds: SideId[],
  library: Material[],
): PageBomRow[] {
  const pageNodes = nodes.filter(n => sideIds.includes(n.sideId));
  const buckets = new Map<string, PageBomRow & { _density: number; _sideSet: Set<SideId> }>();
  for (const n of pageNodes) {
    const sorted = [n.geometry.width, n.geometry.height, n.geometry.depth].sort((a, b) => a - b);
    const spec = `${sorted[0]}×${sorted[1]}×${sorted[2]}`;
    const key = `${n.material}|${spec}`;
    let row = buckets.get(key);
    if (!row) {
      const mat = library.find(m => m.key === n.material);
      row = {
        spec,
        count: 0,
        materialName: mat?.name || `M${n.material}`,
        totalAreaM2: 0,
        totalVolumeM3: 0,
        totalWeightKg: 0,
        fromSides: [],
        _density: mat?.densityKgPerM3 || 0,
        _sideSet: new Set(),
      };
      buckets.set(key, row);
    }
    const volM3 = (n.geometry.width * n.geometry.height * n.geometry.depth) / 1e9;
    row.count += 1;
    row.totalAreaM2 += (sorted[1] * sorted[2]) / 1e6;
    row.totalVolumeM3 += volM3;
    row.totalWeightKg += volM3 * row._density;
    row._sideSet.add(n.sideId);
  }
  return Array.from(buckets.values())
    .map(({ _density, _sideSet, ...rest }) => ({ ...rest, fromSides: [..._sideSet] }))
    .sort((a, b) => b.totalVolumeM3 - a.totalVolumeM3);
}

export const A4_LANDSCAPE_PX = { width: 1684, height: 1191 } as const;

/** A4 横向 1684px = 297mm（200dpi）→ px↔mm 转换系数 */
const PX_TO_MM = 297 / A4_LANDSCAPE_PX.width;

export const PAGE_PARTS = [
  { pageNo: 1, label: '3D', sideIds: [] as SideId[] },
  { pageNo: 2, label: '底座', sideIds: ['pedestal'] as SideId[] },
  { pageNo: 3, label: '侧面', sideIds: ['sideFront'] as SideId[] },
  { pageNo: 4, label: '端面', sideIds: ['endLeft'] as SideId[] },
  { pageNo: 5, label: '顶盖', sideIds: ['topCap'] as SideId[] },
] as const;

export type PageNo = 1 | 2 | 3 | 4 | 5;

/** 每个面对应的平面投影坐标轴（U=横、V=纵） */
const SIDE_PROJECTION: Record<SideId, { u: 'x' | 'y' | 'z'; v: 'x' | 'y' | 'z' }> = {
  pedestal: { u: 'x', v: 'z' },
  topCap:   { u: 'x', v: 'z' },
  sideFront:{ u: 'x', v: 'y' },
  sideBack: { u: 'x', v: 'y' },
  endLeft:  { u: 'z', v: 'y' },
  endRight: { u: 'z', v: 'y' },
};

const AXIS_TO_GEO: Record<'x' | 'y' | 'z', 'width' | 'height' | 'depth'> = {
  x: 'width',
  y: 'height',
  z: 'depth',
};

export interface DrawingContext {
  companyName: string;            // 顶部主标题，如 "Local Packaging"
  watermark: string;              // 背景水印
  customerName: string;
  customerId: string | number;
  orderNo: string;
  innerSize: { length: number; width: number; height: number };
  outerSize: { length: number; width: number; height: number };
  cargoSize?: { length: number; width: number; height: number };
  cargoWeight?: number;           // kg
  crateWeight?: number;           // kg, undefined 时显示 undefined
  designerName: string;
  reviewerName: string;
  drawingNumber: string;          // 图纸编号
  makeDate: string;               // YYYY-MM-DD
  auditDate: string;              // YYYY-MM-DD
  threeDSnapshotDataUrl: string | null;
  explodedSnapshotDataUrl?: string | null;   // 真实 3D 爆炸视图（page 1 右半），缺省时回退到六面十字色块
  pageThumbnails?: Record<number, string>;   // 第 N 页的 3D 缩图 dataUrl（page 2-5 底部条带）
  renderNodes: RenderNode[];      // 已解算的板条节点，用于 2-5 页平面图
  bomLines: BomLine[];            // BOM 行项，用于材料表
  materialLibrary: Material[];    // 材料库（含 textureUrl），用于木纹填充
}

/** 把指定页画到给定 canvas（同步绘制 + 异步图片加载完成） */
export async function renderPageToCanvas(
  canvas: HTMLCanvasElement,
  pageNo: PageNo,
  ctx: DrawingContext,
  totalPages: number,
): Promise<void> {
  canvas.width = A4_LANDSCAPE_PX.width;
  canvas.height = A4_LANDSCAPE_PX.height;
  const g = canvas.getContext('2d');
  if (!g) throw new Error('无法获取 2D context');

  const { width: W, height: H } = canvas;
  const part = PAGE_PARTS.find(p => p.pageNo === pageNo)?.label ?? '3D';

  // 1. 白底
  g.fillStyle = '#ffffff';
  g.fillRect(0, 0, W, H);

  // 3. 顶部公司名 + 分割线 — 仅第 1 页画
  if (pageNo === 1) {
    g.fillStyle = '#1a1612';
    g.font = 'bold 36px "Microsoft YaHei", sans-serif';
    g.textAlign = 'center';
    g.fillText(ctx.companyName, W / 2, 88);

    g.strokeStyle = '#1a1612';
    g.lineWidth = 1;
    g.beginPath();
    g.moveTo(40, 115);
    g.lineTo(W - 40, 115);
    g.stroke();
  }

  // 4.5 预加载本页涉及的木纹贴图
  const partInfo = PAGE_PARTS.find(p => p.pageNo === pageNo);
  const relevantNodes = pageNo === 1
    ? ctx.renderNodes
    : ctx.renderNodes.filter(n => partInfo!.sideIds.includes(n.sideId));
  const textures = await loadMaterialTextures(relevantNodes, ctx.materialLibrary);

  // 4.6 预加载本页 3D 缩图（仅 pageNo 2-5 用，page 1 直接走 threeDSnapshotDataUrl/explodedSnapshotDataUrl）
  const urlsToPreload: string[] = [];
  if (pageNo !== 1 && ctx.pageThumbnails?.[pageNo]) {
    urlsToPreload.push(ctx.pageThumbnails[pageNo]);
  }
  await Promise.all(urlsToPreload.map(async (url) => {
    if (thumbnailImageCache.has(url)) return;
    try {
      const img = new Image();
      await new Promise<void>((res, rej) => {
        img.onload = () => res();
        img.onerror = () => rej(new Error('img load failed'));
        img.src = url;
      });
      thumbnailImageCache.set(url, img);
    } catch {/* ignore */}
  }));

  // 5. 中间渲染区
  // 用户反馈"PDF 内元素整体偏小"：渲染区上下都让出空间，配合放大后的字号
  // page 1: 顶部留 145 给公司名 + 分割线；page 2-5: 顶部 70 + 底部条带加高 → 渲染区 h 从 H-290 减到 H-360
  const renderArea = pageNo === 1
    ? { x: 60, y: 145, w: W - 120, h: H - 365 }
    : { x: 60, y: 70,  w: W - 120, h: H - 360 };
  if (pageNo === 1) {
    await drawAssemblyAndExploded(g, renderArea, ctx, textures);
  } else {
    drawSidePlanView(g, renderArea, pageNo, ctx, textures);
  }

  // 6. 水印（旋转 -20°，半透明灰）
  if (ctx.watermark) {
    g.save();
    g.translate(W / 2, H / 2);
    g.rotate(-Math.PI / 9);
    g.font = 'bold 120px "Microsoft YaHei", sans-serif';
    g.fillStyle = 'rgba(170, 170, 170, 0.25)';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(ctx.watermark, 0, 0);
    g.restore();
  }

  // 7-8. 底部条带：第 1 页 = 说明 + 标题栏；第 2-5 页 = 说明 + 材料表 + 标题栏
  drawBottomBand(g, ctx, pageNo, part, totalPages);

  // 9. 底部版权声明
  g.fillStyle = '#5c4a38';
  g.font = '18px "Microsoft YaHei", sans-serif';
  g.textAlign = 'center';
  g.fillText('版权所有，未经允许不可使用图纸中的设计', W / 2, H - 25);
}

/**
 * 底部条带：3 列布局（说明 / 材料表 / 标题栏）
 * 第 1 页：只画说明 + 标题栏（材料表跳过）
 */
function drawBottomBand(
  g: CanvasRenderingContext2D,
  ctx: DrawingContext,
  pageNo: PageNo,
  part: string,
  totalPages: number,
): void {
  const { width: W, height: H } = g.canvas;
  // 用户反馈"4 视图缩小 + 缩图放大" → 底部条带加高 70px，挤压上方渲染区
  const y = H - 285;
  const h = 240;
  const innerW = W - 120;

  if (pageNo === 1) {
    // 50 / 50 布局
    const halfW = innerW / 2 - 20;
    drawDescriptionBlock(g, ctx, { x: 60, y, w: halfW, h }, null);
    drawTitleBlock(g, ctx, part, pageNo, totalPages, { x: W - 60 - halfW, y, w: halfW, h });
    return;
  }

  // 第 2-5 页：2 列布局（拆解图挪到主区，底部条带删缩图）材料表(70) / 标题栏(30)
  const tableW = Math.floor(innerW * 0.70);
  const titleW = innerW - tableW - 10;
  const tableX = 60;
  const titleX = tableX + tableW + 10;

  drawBandMaterialsTable(g, ctx, pageNo, { x: tableX, y, w: tableW, h });
  drawTitleBlock(g, ctx, part, pageNo, totalPages, { x: titleX, y, w: titleW, h });
}

/** 底部条带最左：当前页对应面的 3D 缩图 */
function drawPageThumbnail(
  g: CanvasRenderingContext2D,
  ctx: DrawingContext,
  pageNo: PageNo,
  box: { x: number; y: number; w: number; h: number },
): void {
  const { x, y, w, h } = box;
  // 用户反馈"去掉左下角 2D 图外面的表格" → 删边框 + 删 4px 内边距，让缩图占满整个 box

  const url = ctx.pageThumbnails?.[pageNo];
  if (!url) {
    g.fillStyle = '#8a7456';
    g.font = '15px "Microsoft YaHei", sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('（3D 缩图缺失）', x + w / 2, y + h / 2);
    return;
  }
  // 异步图片加载非阻塞
  const img = thumbnailImageCache.get(url);
  if (img) {
    drawImageContain(g, img, { x, y, w, h });
  } else {
    // 触发后台加载
    const image = new Image();
    image.onload = () => { thumbnailImageCache.set(url, image); };
    image.src = url;
    g.fillStyle = '#8a7456';
    g.font = '14px "Microsoft YaHei", sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('加载中...', x + w / 2, y + h / 2);
  }
}

const thumbnailImageCache = new Map<string, HTMLImageElement>();

/**
 * 主区左半的"该面拆解图"（page 2-5 用）：
 * 数据来源 = ctx.pageThumbnails[pageNo]，由 DesignWorkspace 调用 threeRenderer
 * 以 explodeMode='layer' 渲染（按 layerIndex 阶梯偏移，让该面的几层板分开来）
 */
function drawExplodedSubview(
  g: CanvasRenderingContext2D,
  ctx: DrawingContext,
  pageNo: PageNo,
  box: { x: number; y: number; w: number; h: number },
): void {
  const { x, y, w, h } = box;

  // 顶部标题
  g.fillStyle = '#5c4a38';
  g.font = '20px "Microsoft YaHei", sans-serif';
  g.textAlign = 'left';
  g.textBaseline = 'top';
  g.fillText('拆解图', x + 4, y + 4);

  // 图片区域（标题下方留 30px）
  const imgArea = { x, y: y + 30, w, h: h - 30 };
  const url = ctx.pageThumbnails?.[pageNo];
  if (!url) {
    g.fillStyle = '#8a7456';
    g.font = '15px "Microsoft YaHei", sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('（拆解图缺失）', imgArea.x + imgArea.w / 2, imgArea.y + imgArea.h / 2);
    return;
  }
  const img = thumbnailImageCache.get(url);
  if (img) {
    drawImageContain(g, img, imgArea);
  } else {
    // 触发后台加载
    const image = new Image();
    image.onload = () => { thumbnailImageCache.set(url, image); };
    image.src = url;
    g.fillStyle = '#8a7456';
    g.font = '14px "Microsoft YaHei", sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('加载中...', imgArea.x + imgArea.w / 2, imgArea.y + imgArea.h / 2);
  }
}

function drawImageContain(
  g: CanvasRenderingContext2D,
  img: HTMLImageElement,
  area: { x: number; y: number; w: number; h: number },
): void {
  const iw = img.naturalWidth, ih = img.naturalHeight;
  if (!iw || !ih) return;
  const r = Math.min(area.w / iw, area.h / ih);
  const dw = iw * r, dh = ih * r;
  const dx = area.x + (area.w - dw) / 2;
  const dy = area.y + (area.h - dh) / 2;
  g.drawImage(img, dx, dy, dw, dh);
}

function drawDescriptionBlock(
  g: CanvasRenderingContext2D,
  ctx: DrawingContext,
  box: { x: number; y: number; w: number; h: number },
  scale?: PageScaleInfo | null,
): void {
  const { x, y, w, h } = box;
  void w; void h;  // 不再画外框

  g.fillStyle = '#1a1612';
  g.font = '20px "Microsoft YaHei", sans-serif';
  g.textAlign = 'left';
  g.textBaseline = 'top';
  g.fillText('说明：', x + 10, y + 10);

  const lines: string[] = [
    `1. 内尺寸：${formatDim(ctx.innerSize)} (mm)`,
    `2. 外尺寸：${formatDim(ctx.outerSize)} (mm)`,
    `3. 内装物重量：${ctx.cargoWeight !== undefined ? ctx.cargoWeight + 'Kg' : 'undefined(Kg)'}`,
    `4. 木箱重量：${ctx.crateWeight !== undefined ? ctx.crateWeight + 'Kg' : 'undefined(Kg)'}`,
  ];
  // 第 2-5 页追加比例字段（DRAWING-DESIGN.md §8.2 第 1 层）
  if (scale) {
    if (scale.isDistorted) {
      lines.push(`5. 比例：水平 1:${scale.ratioH}（厚 ×${scale.amplify.toFixed(1)} 放大）`);
    } else {
      lines.push(`5. 比例：1:${scale.ratioH}（mm）`);
    }
  }
  g.font = w < 500 ? '15px "Microsoft YaHei", sans-serif' : '18px "Microsoft YaHei", sans-serif';
  lines.forEach((line, i) => {
    g.fillText(line, x + 10, y + 42 + i * 26);
  });
}

/** 底部条带中间的紧凑材料表（仅第 2-5 页使用）*/
function drawBandMaterialsTable(
  g: CanvasRenderingContext2D,
  ctx: DrawingContext,
  pageNo: PageNo,
  box: { x: number; y: number; w: number; h: number },
): void {
  const part = PAGE_PARTS.find(p => p.pageNo === pageNo);
  if (!part) return;
  const sideIds = part.sideIds as SideId[];
  // 关键：按当前页面 sideIds 过滤 renderNodes 后重新聚合，
  // 否则同规格跨面合并的 BomLine 会把"底+顶"的 count 一起算到底座页。
  const lines = summarizePageBom(ctx.renderNodes, sideIds, ctx.materialLibrary);

  const { x, y, w, h } = box;

  // 外框
  g.strokeStyle = '#1a1612';
  g.lineWidth = 1;
  g.strokeRect(x, y, w, h);

  // 紧凑列：序号 / 规格 / 数量 / 材料（按用户要求去掉"平方/立方"列）
  const colDefs = [
    { label: '序号', frac: 0.08 },
    { label: '尺寸 (长×宽×高)', frac: 0.42 },
    { label: '数量', frac: 0.12 },
    { label: '材料', frac: 0.38 },
  ];
  const cols = colDefs.map(c => ({ label: c.label, w: w * c.frac }));

  const rowH = 28;
  const headerY = y;

  // 表头
  let cx = x;
  g.fillStyle = '#f4ede0';
  g.fillRect(x, headerY, w, rowH);
  g.strokeStyle = '#1a1612';
  g.lineWidth = 0.8;
  g.font = 'bold 15px "Microsoft YaHei", sans-serif';
  g.textBaseline = 'middle';
  g.textAlign = 'center';
  for (const col of cols) {
    g.strokeRect(cx, headerY, col.w, rowH);
    g.fillStyle = '#1a1612';
    g.fillText(col.label, cx + col.w / 2, headerY + rowH / 2);
    cx += col.w;
  }

  // 行
  const maxRows = Math.floor((h - rowH) / rowH);
  const rows = lines.slice(0, maxRows);
  g.font = '14px "Geist Mono", monospace';
  for (let i = 0; i < rows.length; i++) {
    const line = rows[i];
    const ry = headerY + (i + 1) * rowH;
    cx = x;
    for (const col of cols) {
      g.strokeRect(cx, ry, col.w, rowH);
      cx += col.w;
    }
    const cells: string[] = [
      String(i + 1),
      line.spec,
      String(line.count),
      line.materialName,
    ];
    cx = x;
    g.fillStyle = '#1a1612';
    g.textAlign = 'center';
    for (let c = 0; c < cols.length; c++) {
      // 长文本截断
      const cell = cells[c];
      const maxLen = Math.floor(cols[c].w / 9);  // 字号 14px → 9px/char 近似
      const displayed = cell.length > maxLen ? cell.slice(0, maxLen - 1) + '…' : cell;
      g.fillText(displayed, cx + cols[c].w / 2, ry + rowH / 2);
      cx += cols[c].w;
    }
  }

  if (rows.length === 0) {
    g.fillStyle = '#8a7456';
    g.textAlign = 'center';
    g.font = '15px "Microsoft YaHei", sans-serif';
    g.fillText('（无材料）', x + w / 2, y + h / 2);
  }
  if (lines.length > maxRows) {
    g.fillStyle = '#b84c3a';
    g.textAlign = 'right';
    g.font = '10px "Microsoft YaHei", sans-serif';
    g.fillText(`+${lines.length - maxRows} 项见料单`, x + w - 4, y + h - 4);
  }
}

function drawTitleBlock(
  g: CanvasRenderingContext2D,
  ctx: DrawingContext,
  part: string,
  pageNo: PageNo,
  totalPages: number,
  box: { x: number; y: number; w: number; h: number },
): void {
  const { x, y, w, h } = box;
  const isPage1 = pageNo === 1;

  // 顶部公司名条 — 仅 page 1 画（page 2-5 用户反馈"清除重复 Local Packaging"）
  let rowsStartY = y;
  if (isPage1) {
    g.strokeStyle = '#1a1612';
    g.lineWidth = 1;
    g.strokeRect(x, y, w, 40);
    g.fillStyle = '#1a1612';
    g.font = `bold ${w < 500 ? 23 : 28}px "Microsoft YaHei", sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(ctx.companyName, x + w / 2, y + 20);
    rowsStartY = y + 40;
  }

  // 下面 5 行 × 2 列表格
  const rows: Array<[string, string, string, string]> = [
    ['客户：', String(ctx.customerName || '-'), '', ''],
    ['客户ID：', String(ctx.customerId || '-'), '部位：', part],
    ['制图：', String(ctx.designerName || '-'), '制图时间：', ctx.makeDate],
    ['审核：', String(ctx.reviewerName || '-'), '审核时间：', ctx.auditDate],
    ['图纸编号：', ctx.drawingNumber || '-', `${pageNo}/${totalPages}`, ''],
  ];

  // 行高动态：page 1 公司名条 40 + 5 行；page 2-5 5 行填满整个 box.h
  const availH = h - (isPage1 ? 40 : 0);
  const rowH = Math.floor(availH / rows.length);
  const isNarrow = w < 500;
  const labelW = isNarrow ? 70 : 120;
  const colW = w / 2;

  g.font = `${isNarrow ? 14 : 18}px "Microsoft YaHei", sans-serif`;
  g.textBaseline = 'middle';

  rows.forEach((row, i) => {
    const ry = rowsStartY + i * rowH;
    g.strokeRect(x, ry, w, rowH);

    g.textAlign = 'left';
    g.fillText(row[0], x + 8, ry + rowH / 2);

    if (row[2]) {
      // 双列：左半 + 右半，画中间竖线
      g.beginPath();
      g.moveTo(x + colW, ry);
      g.lineTo(x + colW, ry + rowH);
      g.stroke();
      // 左半值
      g.fillText(row[1], x + labelW, ry + rowH / 2, colW - labelW - 8);
      // 右半 label + 值
      g.fillText(row[2], x + colW + 8, ry + rowH / 2);
      g.fillText(row[3], x + colW + labelW, ry + rowH / 2, w - colW - labelW - 8);
    } else {
      // 单列：值跨整行宽（如客户名），不画中间竖线，长字符串自动压缩
      g.fillText(row[1], x + labelW, ry + rowH / 2, w - labelW - 12);
    }
  });
}

function formatDim(d: { length: number; width: number; height: number }): string {
  return `${d.length}*${d.width}*${d.height}`;
}

/**
 * 第一页：组装图（左 = 3D 截图）+ 分解图（右 = 六面十字展开）
 */
async function drawAssemblyAndExploded(
  g: CanvasRenderingContext2D,
  area: { x: number; y: number; w: number; h: number },
  ctx: DrawingContext,
  textures: Map<number, HTMLImageElement>,
): Promise<void> {
  const halfW = (area.w - 24) / 2;
  const leftArea = { x: area.x, y: area.y, w: halfW, h: area.h };
  const rightArea = { x: area.x + halfW + 24, y: area.y, w: halfW, h: area.h };

  // 标题
  g.fillStyle = '#5c4a38';
  g.font = '26px "Microsoft YaHei", sans-serif';
  g.textAlign = 'left';
  g.fillText('组装图', leftArea.x + 8, leftArea.y + 4);
  g.fillText('分解图', rightArea.x + 8, rightArea.y + 4);

  // 不再画外边框 — 用户反馈"不要用表格框起来"，3D 图本身有内容边界，多余的框反而显得拥挤

  // 左：3D 截图
  if (ctx.threeDSnapshotDataUrl) {
    await drawImageDataUrl(g, ctx.threeDSnapshotDataUrl, {
      x: leftArea.x + 8, y: leftArea.y + 24, w: leftArea.w - 16, h: leftArea.h - 40,
    });
  } else {
    g.fillStyle = '#8a7456';
    g.font = '23px "Microsoft YaHei", sans-serif';
    g.textAlign = 'center';
    g.fillText('（3D 截图缺失）', leftArea.x + leftArea.w / 2, leftArea.y + leftArea.h / 2);
  }

  // 右：优先 3D 爆炸视图，缺失则回退到六面十字色块
  const rightInner = {
    x: rightArea.x + 8, y: rightArea.y + 24, w: rightArea.w - 16, h: rightArea.h - 40,
  };
  if (ctx.explodedSnapshotDataUrl) {
    await drawImageDataUrl(g, ctx.explodedSnapshotDataUrl, rightInner);
  } else {
    drawExplodedCrossNet(g, rightInner, ctx, textures);
  }
}

/**
 * 六面十字展开（unfolded box net）：
 *           [topCap]
 *  [endLeft][sideFront][endRight][sideBack]
 *           [pedestal]
 */
function drawExplodedCrossNet(
  g: CanvasRenderingContext2D,
  area: { x: number; y: number; w: number; h: number },
  ctx: DrawingContext,
  textures: Map<number, HTMLImageElement>,
): void {
  const { length: L, width: W, height: H } = ctx.outerSize;
  // 十字布局逻辑：横向 4 块 (endLeft + sideFront + endRight + sideBack)
  //   高度由最高的那块决定
  // 纵向加上 topCap 和 pedestal 在 sideFront 上下
  const totalU = W + L + W + L;   // 端 + 侧 + 端 + 侧 (横向)
  const totalV = W + H + W;        // topCap + sideFront + pedestal (纵向)

  const padding = 24;
  const drawW = area.w - padding * 2;
  const drawH = area.h - padding * 2;
  const scale = Math.min(drawW / totalU, drawH / totalV);

  const finW = totalU * scale;
  const finH = totalV * scale;
  const ox = area.x + (area.w - finW) / 2;
  const oy = area.y + (area.h - finH) / 2;

  // 4 横向块的左边界 x（按 endLeft, sideFront, endRight, sideBack 顺序）
  const xs = {
    endLeft: ox,
    sideFront: ox + W * scale,
    endRight: ox + W * scale + L * scale,
    sideBack: ox + W * scale + L * scale + W * scale,
  };
  // 中间行（横向带）的 y 起点
  const yMid = oy + W * scale;

  const faces: Array<{ id: SideId; x: number; y: number; w: number; h: number; label: string }> = [
    { id: 'topCap',    x: xs.sideFront, y: oy,                   w: L * scale, h: W * scale, label: '顶盖' },
    { id: 'sideFront', x: xs.sideFront, y: yMid,                 w: L * scale, h: H * scale, label: '前侧' },
    { id: 'pedestal',  x: xs.sideFront, y: yMid + H * scale,     w: L * scale, h: W * scale, label: '底座' },
    { id: 'endLeft',   x: xs.endLeft,   y: yMid,                 w: W * scale, h: H * scale, label: '左端' },
    { id: 'endRight',  x: xs.endRight,  y: yMid,                 w: W * scale, h: H * scale, label: '右端' },
    { id: 'sideBack',  x: xs.sideBack,  y: yMid,                 w: L * scale, h: H * scale, label: '后侧' },
  ];

  for (const f of faces) {
    // 优先用整张木纹拉伸（与 boxDesigner 3D 一致）
    const fNodes = ctx.renderNodes.filter(n => n.sideId === f.id);
    const boardNode = fNodes.find(n => n.elementType === 'board') || fNodes[0];
    const img = boardNode ? textures.get(boardNode.material) : undefined;
    if (img) {
      g.drawImage(img, f.x, f.y, f.w, f.h);
    } else {
      g.fillStyle = boardNode?.materialcolor || '#e8d4a8';
      g.fillRect(f.x, f.y, f.w, f.h);
    }
    g.strokeStyle = '#1a1612';
    g.lineWidth = 1;
    g.strokeRect(f.x, f.y, f.w, f.h);
    // 面标签：白色描边 + 黑字，提高在木纹上的可读性
    g.font = `bold ${Math.max(13, Math.min(20, f.w / 7))}px "Microsoft YaHei", sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.lineWidth = 3;
    g.strokeStyle = 'rgba(255,255,255,0.9)';
    g.strokeText(f.label, f.x + f.w / 2, f.y + f.h / 2);
    g.fillStyle = '#1a1612';
    g.fillText(f.label, f.x + f.w / 2, f.y + f.h / 2);
  }

  // 外轮廓总尺寸标注：横向 (W+L+W+L)、纵向 (W+H+W)
  g.fillStyle = '#5c4a38';
  g.font = '15px "Geist Mono", monospace';
  g.textAlign = 'center';
  g.textBaseline = 'top';
  g.fillText(`横向展开总长 ${Math.round(totalU)} mm`, area.x + area.w / 2, area.y + area.h - 16);
}

/**
 * 第 2-5 页：四视图布局（local-clone 制图法）
 *   ┌──────────┬──────────┐
 *   │ 长边截面 │ 短边截面 │   <- 上行：两个截面，所有 layer 厚度堆叠
 *   ├──────────┼──────────┤
 *   │ 内/顶视图│ 外/底视图│   <- 下行：按 elementType 分层
 *   └──────────┴──────────┘   板进内/顶，框架/横方进外/底
 */
/**
 * 子视图（截面图 / 面视图）内边距 — 单一来源
 * scale 计算（drawSidePlanView / computePageScale）与 drawer 内部都引用此常量
 * 改这里 = 三处自动同步，杜绝"算 scale 估 60、画图用 86"那种漂移
 *  - left   50 = 左侧厚度数字标注（"18" 等 2 位数 ≈ 20px + 引出线 + 余量）
 *  - right  55 = 右侧总尺寸数字（4 位数 ≈ 32px + 尺寸线偏移 14 + 余量 9）
 *  - top    22 = 顶部标题 + 顶部总尺寸线
 *  - bottom 50 = 底部分段尺寸（面视图最厚）
 */
const SUBVIEW_PAD = { left: 50, right: 55, top: 22, bottom: 50 } as const;

function drawSidePlanView(
  g: CanvasRenderingContext2D,
  area: { x: number; y: number; w: number; h: number },
  pageNo: PageNo,
  ctx: DrawingContext,
  _textures: Map<number, HTMLImageElement>,
): void {
  const part = PAGE_PARTS.find(p => p.pageNo === pageNo);
  if (!part) return;
  const sideIds = part.sideIds;
  const allNodes = ctx.renderNodes.filter(n => sideIds.includes(n.sideId));

  if (allNodes.length === 0) {
    g.fillStyle = '#8a7456';
    g.font = '30px "Microsoft YaHei", sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(`${part.label} 暂无构件`, area.x + area.w / 2, area.y + area.h / 2);
    return;
  }

  const axes = getSideAxes(sideIds[0]);

  // 跨度（mm）：长边、短边、厚度
  const longSpan = axisSpan(allNodes, axes.longEdge);
  const shortSpan = axisSpan(allNodes, axes.shortEdge);
  const thickSpan = axisSpan(allNodes, axes.thickness);

  // 主区分左右两栏：左 50% 拆解图（该面 layer-explode 渲染）/ 右 50% 原 4 视图 2x2 紧凑布局
  const mainColGap = 22;
  const leftW = Math.floor((area.w - mainColGap) / 2);
  const rightW = area.w - leftW - mainColGap;

  // 左半：拆解图（从 ctx.pageThumbnails 取 layer-explode 渲染图）
  drawExplodedSubview(g, ctx, pageNo, { x: area.x, y: area.y, w: leftW, h: area.h });

  // 右半 4 视图 2x2 布局
  const subArea = { x: area.x + leftW + mainColGap, y: area.y, w: rightW, h: area.h };
  const colGap = 22;
  const rowGap = 14;
  const colW = (subArea.w - colGap) / 2;
  const topRowH = Math.floor(subArea.h * 0.28);
  const bottomRowH = subArea.h - topRowH - rowGap;

  // 方案 G：area 固定 + 内容归一化（stretch-to-fit）
  // 视图轮廓永远 = area 尺寸，箱子真实长宽比不影响 PDF 上的视图形状
  // drawer 内部按归一化坐标 (pos - min) / span × drawW 自己算，scale 参数仅占位
  const scaleH = 0, scaleV = 0, scaleFaceV = 0;
  void longSpan; void shortSpan; void thickSpan;

  const a_long  = { x: subArea.x,                  y: subArea.y,                       w: colW, h: topRowH };
  const a_short = { x: subArea.x + colW + colGap,   y: subArea.y,                       w: colW, h: topRowH };
  const a_inner = { x: subArea.x,                  y: subArea.y + topRowH + rowGap,    w: colW, h: bottomRowH };
  const a_outer = { x: subArea.x + colW + colGap,   y: subArea.y + topRowH + rowGap,    w: colW, h: bottomRowH };

  // 长边截面 = 沿短边方向中切，看长边×厚度剖面；短边截面反之
  drawSchematicCrossSection(g, a_long, '长边截面', axes.longEdge, axes.thickness, axes.shortEdge, allNodes, scaleH, scaleV);
  drawSchematicCrossSection(g, a_short, '短边截面', axes.shortEdge, axes.thickness, axes.longEdge, allNodes, scaleH, scaleV);

  // 内侧/顶部 = 最高 layer 的覆盖板（朝内/朝上的那一面）
  // 外侧/底部 = 其余 layer 的所有节点（框架 + 中央分隔板 + 横方）→ 形成"田字"结构
  // 注意：不能按 elementType 分，否则像 sideFront 中央分隔板（也是 board 类型）会被错误过滤
  const maxLayer = allNodes.reduce((m, n) => Math.max(m, n.layerIndex), 0);
  const innerNodes = allNodes.filter(n => n.layerIndex === maxLayer);
  const outerNodes = allNodes.filter(n => n.layerIndex !== maxLayer);
  drawSchematicFaceView(g, a_inner, axes.innerLabel, axes.longEdge, axes.shortEdge, innerNodes, allNodes, scaleH, scaleFaceV);
  drawSchematicFaceView(g, a_outer, axes.outerLabel, axes.longEdge, axes.shortEdge, outerNodes.length ? outerNodes : innerNodes, allNodes, scaleH, scaleFaceV);
}

function axisSpan(nodes: RenderNode[], axis: 'x' | 'y' | 'z'): number {
  let lo = Infinity, hi = -Infinity;
  for (const n of nodes) {
    const half = n.geometry[AXIS_TO_GEO[axis]] / 2;
    lo = Math.min(lo, n.position[axis] - half);
    hi = Math.max(hi, n.position[axis] + half);
  }
  return Math.max(1, hi - lo);
}

/* ================================================================
   工程制图工具函数
   规范来源：DRAWING-DESIGN.md (GB/T 17452 + GB/T 17453.4 + ISO 128)
   ================================================================ */

/**
 * 线型层次（ISO 128-22）
 * 详见 DRAWING-DESIGN.md §4
 *  - thick   1.5px 实线 — 可见构件外轮廓、剖切到的实体边缘
 *  - thin    0.5px 实线 — 尺寸线、尺寸界线、引出线、构件内分割
 *  - dashdot 0.8px 点画线 — 中心线、对称线、剖切位置线
 *  - dashed  0.6px 虚线 — 不可见轮廓、隐藏边缘
 */
function setLineStyle(
  g: CanvasRenderingContext2D,
  kind: 'thick' | 'thin' | 'dashdot' | 'dashed',
): void {
  switch (kind) {
    case 'thick':   g.lineWidth = 1.5; g.setLineDash([]); break;
    case 'thin':    g.lineWidth = 0.5; g.setLineDash([]); break;
    case 'dashdot': g.lineWidth = 0.8; g.setLineDash([8, 2, 1, 2]); break;
    case 'dashed':  g.lineWidth = 0.6; g.setLineDash([4, 2]); break;
  }
  g.strokeStyle = '#1a1612';
}

/**
 * 材料图案填充（GB/T 17453.4 简化版）
 * 详见 DRAWING-DESIGN.md §6
 *  - board      → 45° 平行斜线（木材标准图例）
 *  - frame      → 粗交叉斜线（结构方木）
 *  - horBrace   → 45° 反向斜线（与 board 对称，区分方向）
 *  - bevelBrace → 中心点阵（小型零件）
 * 缓存：每个 canvas context 缓存 4 种 pattern。
 */
const patternCache = new WeakMap<CanvasRenderingContext2D, Map<string, CanvasPattern>>();
function getOrCreatePattern(
  g: CanvasRenderingContext2D,
  elementType: string,
): CanvasPattern | null {
  let cache = patternCache.get(g);
  if (!cache) {
    cache = new Map();
    patternCache.set(g, cache);
  }
  const cached = cache.get(elementType);
  if (cached) return cached;

  const tile = document.createElement('canvas');
  tile.width = tile.height = 8;
  const t = tile.getContext('2d');
  if (!t) return null;

  // 浅奶油底色 — 让黑色斜线在白纸上呈现"材料感"
  t.fillStyle = '#fdfaf3';
  t.fillRect(0, 0, 8, 8);
  t.strokeStyle = '#1a1612';
  t.lineWidth = 0.6;
  t.beginPath();
  if (elementType === 'board') {
    t.moveTo(-1, 9); t.lineTo(9, -1);
    t.moveTo(3, 11); t.lineTo(11, 3);
    t.stroke();
  } else if (elementType === 'frame') {
    t.moveTo(-1, 9); t.lineTo(9, -1);
    t.moveTo(-1, -1); t.lineTo(9, 9);
    t.lineWidth = 0.9;
    t.stroke();
  } else if (elementType === 'horBrace') {
    t.moveTo(-1, -1); t.lineTo(9, 9);
    t.moveTo(-1, 3); t.lineTo(5, 9);
    t.stroke();
  } else {
    // bevelBrace 或未知 → 中心点阵
    t.fillStyle = '#1a1612';
    t.fillRect(3, 3, 1.5, 1.5);
  }
  const pat = g.createPattern(tile, 'repeat');
  if (pat) cache.set(elementType, pat);
  return pat;
}

/** 用图案填充矩形 node。降级路径：纯奶油色 #fdfaf3。 */
function fillNodeWithPattern(
  g: CanvasRenderingContext2D,
  n: RenderNode,
  px: number, py: number, pw: number, ph: number,
): void {
  const pat = getOrCreatePattern(g, n.elementType);
  g.fillStyle = pat || '#fdfaf3';
  g.fillRect(px, py, pw, ph);
}

/** 中文构件标签（图例 / 引出线用） */
const ELEMENT_TYPE_LABEL: Record<string, string> = {
  board: '板',
  frame: '框架',
  horBrace: '横方',
  bevelBrace: '垫块',
};

/**
 * 构件引出线（DRAWING-DESIGN.md §9.3）
 * 按 (elementType, sorted geometry) 把 nodes 分组，取每组第一个节点画一根引出线 + 规格文字。
 * 标签位置：面视图右上角内侧，半透明白底确保可读性。
 * 最多 3 条引出线（避免视觉过载）。
 */
function drawComponentLeaders(
  g: CanvasRenderingContext2D,
  drawNodes: RenderNode[],
  uAxis: 'x' | 'y' | 'z',
  vAxis: 'x' | 'y' | 'z',
  uMin: number,
  vMin: number,
  scaleU: number,
  scaleV: number,
  ox: number,
  oy: number,
  finW: number,
  finH: number,
): void {
  // 1. 分组：按 (elementType, sorted geometry) 取代表
  const groups = new Map<string, { type: string; spec: string; node: RenderNode }>();
  for (const n of drawNodes) {
    const dims = [n.geometry.width, n.geometry.height, n.geometry.depth].sort((a, b) => a - b);
    const r0 = Math.round(dims[0]);
    const r1 = Math.round(dims[1]);
    // 截面规格：板用厚度，框架/横方/垫块用最小×中等
    const spec = (n.elementType === 'board' && r0 === r1) ? `${r0}` : `${r0}×${r1}`;
    const key = `${n.elementType}|${spec}`;
    if (!groups.has(key)) {
      groups.set(key, { type: n.elementType, spec, node: n });
    }
  }
  const items = Array.from(groups.values()).slice(0, 3);
  if (items.length === 0) return;

  // 2. 标签区：面视图内右上角，宽 90px，半透明白底
  const labelStripW = 92;
  const labelStripX = ox + finW - labelStripW - 6;
  const labelStripY = oy + 6;
  const lineH = 16;
  const stripH = items.length * lineH + 8;

  g.save();
  g.fillStyle = 'rgba(255, 255, 255, 0.78)';
  g.fillRect(labelStripX, labelStripY, labelStripW, stripH);
  // 标签区淡色边框（细实线，便于识别为标注层）
  setLineStyle(g, 'thin');
  g.strokeStyle = '#8a7456';
  g.strokeRect(labelStripX, labelStripY, labelStripW, stripH);

  // 3. 逐条画引出线 + 圆点 + 文字
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const u = item.node.position[uAxis];
    const v = item.node.position[vAxis];
    const cx = ox + (u - uMin) * scaleU;
    const cy = oy + finH - (v - vMin) * scaleV;
    const textY = labelStripY + 12 + i * lineH;
    const textX = labelStripX + 6;

    // 引出线（细实线，黑色）：构件中心 → 短斜线 → 水平到标签前
    setLineStyle(g, 'thin');
    g.strokeStyle = '#1a1612';
    const elbowX = labelStripX - 6;
    const elbowY = textY;
    g.beginPath();
    g.moveTo(cx, cy);
    g.lineTo(elbowX - 8, elbowY);
    g.lineTo(elbowX, elbowY);
    g.stroke();

    // 起点实心圆点（指向构件）
    g.fillStyle = '#1a1612';
    g.beginPath();
    g.arc(cx, cy, 1.8, 0, Math.PI * 2);
    g.fill();

    // 文字
    const text = `${ELEMENT_TYPE_LABEL[item.type] || item.type} ${item.spec}`;
    g.font = '14px "Microsoft YaHei", sans-serif';
    g.fillStyle = '#1a1612';
    g.textAlign = 'left';
    g.textBaseline = 'middle';
    g.fillText(text, textX, textY);
  }
  g.restore();
}

/**
 * 剖切位置标记（GB/T 17452 §4.2）
 * 详见 DRAWING-DESIGN.md §7
 * 结构：
 *   端粗实线（剖切边界，2px × 8px）+ 点画线主体 + 端粗实线 + 字母标记
 * 字母标记位置：
 *   horizontal: 字母在线两端外侧（左/右）
 *   vertical:   字母在剖切线 BOTTOM 端外侧（避免与顶部总尺寸文字冲突）
 */
function drawSectionLine(
  g: CanvasRenderingContext2D,
  label: string,
  orientation: 'horizontal' | 'vertical',
  x0: number, y0: number, x1: number, y1: number,
): void {
  // 1. 中段点画线
  setLineStyle(g, 'dashdot');
  g.beginPath();
  g.moveTo(x0, y0);
  g.lineTo(x1, y1);
  g.stroke();
  g.setLineDash([]);

  // 2. 两端外侧粗短实线（剖切边界，长 8px，粗 2px）
  g.strokeStyle = '#1a1612';
  g.lineWidth = 2;
  g.beginPath();
  if (orientation === 'horizontal') {
    g.moveTo(x0 - 8, y0); g.lineTo(x0, y0);
    g.moveTo(x1, y1);     g.lineTo(x1 + 8, y1);
  } else {
    g.moveTo(x0, y0 - 8); g.lineTo(x0, y0);
    g.moveTo(x1, y1);     g.lineTo(x1, y1 + 8);
  }
  g.stroke();
  g.lineWidth = 1;

  // 3. 字母标记（粗体 14px 黑字）
  g.fillStyle = '#1a1612';
  g.font = 'bold 14px "Microsoft YaHei", sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  if (orientation === 'horizontal') {
    // 两端 A / A
    g.fillText(label, x0 - 18, y0);
    g.fillText(label, x1 + 18, y1);
  } else {
    // 仅底端 B（顶端被总长尺寸占用，避免重叠）
    g.fillText(label, x1 + 10, y1 + 10);
  }
}

/**
 * 把 nodes 沿 vAxis 划分为若干"层"，返回每层的中心 v 坐标和厚度（mm）。
 * 用于在截面图右侧标注每层的真实厚度数字（如 "18" 板厚），让薄板可见。
 */
function collectLayerThicknesses(
  nodes: RenderNode[],
  vAxis: 'x' | 'y' | 'z',
): Array<{ center: number; thickness: number }> {
  // 收集 nodes 在 v 方向的 [min, max] 区段，按 min 排序合并重叠
  const segs: Array<[number, number]> = [];
  for (const n of nodes) {
    const half = n.geometry[AXIS_TO_GEO[vAxis]] / 2;
    segs.push([n.position[vAxis] - half, n.position[vAxis] + half]);
  }
  segs.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const s of segs) {
    if (merged.length && s[0] <= merged[merged.length - 1][1] + 0.5) {
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], s[1]);
    } else {
      merged.push([s[0], s[1]]);
    }
  }
  return merged.map(([lo, hi]) => ({ center: (lo + hi) / 2, thickness: hi - lo }));
}

/**
 * 截面示意图（长边/短边）：
 * 横轴 = h（沿面长度方向），纵轴 = thickness（垂直面方向，放大显示）
 * 把所有 layer 的板/木方沿 thickness 堆叠展示。
 * 厚度方向通常 << 长度方向，自动放大让结构可见。
 */
function drawSchematicCrossSection(
  g: CanvasRenderingContext2D,
  area: { x: number; y: number; w: number; h: number },
  title: string,
  hAxis: 'x' | 'y' | 'z',
  vAxis: 'x' | 'y' | 'z',
  sliceAxis: 'x' | 'y' | 'z',
  nodes: RenderNode[],
  scaleH: number,
  scaleV: number,
): void {
  // 标题
  g.fillStyle = '#5c4a38';
  g.font = '18px "Microsoft YaHei", sans-serif';
  g.textAlign = 'left';
  g.textBaseline = 'top';
  g.fillText(title, area.x + 4, area.y);

  if (nodes.length === 0) return;

  // 用全量 nodes 计算 (h, v) 包围盒，保证两幅截面图共用同一缩放基线
  let hMin = Infinity, hMax = -Infinity, vMin = Infinity, vMax = -Infinity;
  let sliceMin = Infinity, sliceMax = -Infinity;
  for (const n of nodes) {
    const h = n.position[hAxis], v = n.position[vAxis], s = n.position[sliceAxis];
    const hHalf = n.geometry[AXIS_TO_GEO[hAxis]] / 2;
    const vHalf = n.geometry[AXIS_TO_GEO[vAxis]] / 2;
    const sHalf = n.geometry[AXIS_TO_GEO[sliceAxis]] / 2;
    hMin = Math.min(hMin, h - hHalf); hMax = Math.max(hMax, h + hHalf);
    vMin = Math.min(vMin, v - vHalf); vMax = Math.max(vMax, v + vHalf);
    sliceMin = Math.min(sliceMin, s - sHalf); sliceMax = Math.max(sliceMax, s + sHalf);
  }
  const hSpan = hMax - hMin || 1;
  const vSpan = vMax - vMin || 1;
  const slicePlane = (sliceMin + sliceMax) / 2;

  // 切片：保留沿 sliceAxis 跨过中切面的 nodes。0 命中时回退到全量并加示意标记
  const SLICE_EPS = 0.5; // mm 浮点容差
  let slicedNodes = nodes.filter((n) => {
    const s = n.position[sliceAxis];
    const half = n.geometry[AXIS_TO_GEO[sliceAxis]] / 2;
    return s - half - SLICE_EPS <= slicePlane && s + half + SLICE_EPS >= slicePlane;
  });
  let degraded = false;
  if (slicedNodes.length === 0) {
    slicedNodes = nodes;
    degraded = true;
  }

  const padTop = SUBVIEW_PAD.top, padBottom = SUBVIEW_PAD.bottom;
  const padLeft = SUBVIEW_PAD.left, padRight = SUBVIEW_PAD.right;
  const drawW = area.w - padLeft - padRight;
  const drawH = area.h - padTop - padBottom;
  // 方案 H（最终版）：area 固定 + contain-fit 真实长宽比
  // 取最小 scale 让内容完整装进 area，按真实比例画 → 方板画方板、长板画长板
  // 物体在 area 内居中，留白允许（reference 同款做法）
  const scaleFit = Math.min(drawW / hSpan, drawH / vSpan);
  const finW = hSpan * scaleFit;
  const finH = vSpan * scaleFit;
  const ox = area.x + padLeft + (drawW - finW) / 2;
  const oy = area.y + padTop + (drawH - finH) / 2;
  void scaleH; void scaleV; void padRight;

  // 工程制图三层绘制 — 真实比例 contain-fit
  // Pass 1：图案填充
  for (const n of slicedNodes) {
    const h = n.position[hAxis], v = n.position[vAxis];
    const hSize = n.geometry[AXIS_TO_GEO[hAxis]];
    const vSize = n.geometry[AXIS_TO_GEO[vAxis]];
    const px = ox + (h - hSize / 2 - hMin) * scaleFit;
    const py = oy + (vMax - v - vSize / 2) * scaleFit;
    const pw = hSize * scaleFit;
    const ph = vSize * scaleFit;
    fillNodeWithPattern(g, n, px, py, pw, ph);
  }
  // Pass 2：细实线描边
  setLineStyle(g, 'thin');
  for (const n of slicedNodes) {
    const h = n.position[hAxis], v = n.position[vAxis];
    const hSize = n.geometry[AXIS_TO_GEO[hAxis]];
    const vSize = n.geometry[AXIS_TO_GEO[vAxis]];
    const px = ox + (h - hSize / 2 - hMin) * scaleFit;
    const py = oy + (vMax - v - vSize / 2) * scaleFit;
    const pw = hSize * scaleFit;
    const ph = vSize * scaleFit;
    g.strokeRect(px, py, pw, ph);
  }
  g.setLineDash([]);

  // 总长（顶）+ 总厚（右）— 真实比例
  drawDimensionLine(g, { x: ox, y: oy - 12 }, { x: ox + finW, y: oy - 12 }, `${Math.round(hSpan)}`, 'horizontal');
  drawDimensionLine(g, { x: ox + finW + 14, y: oy }, { x: ox + finW + 14, y: oy + finH }, `${Math.round(vSpan)}`, 'vertical');

  // 各层厚度（左）
  const thinLayers = collectLayerThicknesses(slicedNodes, vAxis);
  if (thinLayers.length > 0) {
    g.fillStyle = '#1a1612';
    g.font = '13px "Geist Mono", monospace';
    g.textAlign = 'right';
    g.textBaseline = 'middle';
    for (const layer of thinLayers) {
      const yPx = oy + (vMax - layer.center) * scaleFit;
      g.fillText(String(Math.round(layer.thickness)), ox - 4, yPx);
    }
  }
  void vMin;

  // degraded 警示标已按用户要求去掉
  void degraded;

  // 内部 H 段尺寸（底）— 用切片后的 nodes 边界（更贴合实际剖面）
  const hBoundaries = collectBoundaries(slicedNodes, hAxis);
  if (hBoundaries.length > 2 && hBoundaries.length <= 18) {
    const yLine = oy + finH + 8;
    g.strokeStyle = '#5c4a38';
    g.lineWidth = 0.4;
    g.fillStyle = '#1a1612';
    g.font = '13px "Geist Mono", monospace';
    g.textAlign = 'center';
    g.textBaseline = 'top';
    for (let i = 0; i < hBoundaries.length - 1; i++) {
      const a = hBoundaries[i], b = hBoundaries[i + 1];
      const w = Math.round(b - a);
      if (w < 5) continue;
      const xa = ox + (a - hMin) * scaleFit;
      const xb = ox + (b - hMin) * scaleFit;
      g.beginPath();
      g.moveTo(xa, yLine); g.lineTo(xb, yLine);
      g.moveTo(xa, yLine - 3); g.lineTo(xa, yLine + 3);
      g.moveTo(xb, yLine - 3); g.lineTo(xb, yLine + 3);
      g.stroke();
      g.fillText(String(w), (xa + xb) / 2, yLine + 4);
    }
  }

  // 厚度放大警示标已按用户要求去掉
}

/**
 * 工程图警示标签（红框 + 11px 加粗黑字）
 * 详见 DRAWING-DESIGN.md §8.2 第 3 层
 */
function drawWarningTag(
  g: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  align: 'left' | 'right' = 'left',
): void {
  g.font = 'bold 14px "Microsoft YaHei", sans-serif';
  const padX = 6, padY = 3;
  const tw = g.measureText(text).width;
  const boxW = tw + padX * 2;
  const boxH = 18;
  const boxX = align === 'right' ? x - boxW : x;
  // 浅红底
  g.fillStyle = 'rgba(184, 76, 58, 0.10)';
  g.fillRect(boxX, y, boxW, boxH);
  // 红框
  g.strokeStyle = '#b84c3a';
  g.lineWidth = 1;
  g.setLineDash([]);
  g.strokeRect(boxX, y, boxW, boxH);
  // 文字
  g.fillStyle = '#1a1612';
  g.textAlign = 'left';
  g.textBaseline = 'middle';
  g.fillText(text, boxX + padX, y + boxH / 2);
}

/**
 * 面视图示意（顶部/底部 或 内侧/外侧）：横纵轴都是面平面坐标，等比缩放
 * 仅画 drawNodes（按 elementType 过滤），bbox 用 allNodes 保持 inner/outer 同 scale
 */
function drawSchematicFaceView(
  g: CanvasRenderingContext2D,
  area: { x: number; y: number; w: number; h: number },
  title: string,
  uAxis: 'x' | 'y' | 'z',
  vAxis: 'x' | 'y' | 'z',
  drawNodes: RenderNode[],
  allNodes: RenderNode[],
  scaleU: number,
  scaleV: number,
): void {
  g.fillStyle = '#5c4a38';
  g.font = '18px "Microsoft YaHei", sans-serif';
  g.textAlign = 'left';
  g.textBaseline = 'top';
  g.fillText(title, area.x + 4, area.y);

  if (drawNodes.length === 0) {
    g.fillStyle = '#8a7456';
    g.font = '17px "Microsoft YaHei", sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('（本图无构件）', area.x + area.w / 2, area.y + area.h / 2);
    return;
  }

  // bbox 用 allNodes 保持 inner/outer 同 scale
  let uMin = Infinity, uMax = -Infinity, vMin = Infinity, vMax = -Infinity;
  for (const n of allNodes) {
    const u = n.position[uAxis], v = n.position[vAxis];
    const uHalf = n.geometry[AXIS_TO_GEO[uAxis]] / 2;
    const vHalf = n.geometry[AXIS_TO_GEO[vAxis]] / 2;
    uMin = Math.min(uMin, u - uHalf); uMax = Math.max(uMax, u + uHalf);
    vMin = Math.min(vMin, v - vHalf); vMax = Math.max(vMax, v + vHalf);
  }
  const uSpan = uMax - uMin || 1;
  const vSpan = vMax - vMin || 1;

  const padTop = SUBVIEW_PAD.top, padBottom = SUBVIEW_PAD.bottom;
  const padLeft = SUBVIEW_PAD.left;
  const drawW = area.w - padLeft - SUBVIEW_PAD.right;
  const drawH = area.h - padTop - padBottom;
  // 方案 H：area 固定 + contain-fit 真实长宽比
  // 方板画方板、长板画长板，居中在 area 内，留白允许
  const scaleFit = Math.min(drawW / uSpan, drawH / vSpan);
  const finW = uSpan * scaleFit;
  const finH = vSpan * scaleFit;
  const ox = area.x + padLeft + (drawW - finW) / 2;
  const oy = area.y + padTop + (drawH - finH) / 2;
  void scaleU; void scaleV; void vMax;

  // 真实比例 contain-fit
  // Pass 1：图案填充
  for (const n of drawNodes) {
    const u = n.position[uAxis], v = n.position[vAxis];
    const uSize = n.geometry[AXIS_TO_GEO[uAxis]];
    const vSize = n.geometry[AXIS_TO_GEO[vAxis]];
    const px = ox + (u - uSize / 2 - uMin) * scaleFit;
    const py = oy + (vMax - v - vSize / 2) * scaleFit;
    const pw = uSize * scaleFit;
    const ph = vSize * scaleFit;
    fillNodeWithPattern(g, n, px, py, pw, ph);
  }
  // Pass 2：细实线描边
  setLineStyle(g, 'thin');
  for (const n of drawNodes) {
    const u = n.position[uAxis], v = n.position[vAxis];
    const uSize = n.geometry[AXIS_TO_GEO[uAxis]];
    const vSize = n.geometry[AXIS_TO_GEO[vAxis]];
    const px = ox + (u - uSize / 2 - uMin) * scaleFit;
    const py = oy + (vMax - v - vSize / 2) * scaleFit;
    const pw = uSize * scaleFit;
    const ph = vSize * scaleFit;
    g.strokeRect(px, py, pw, ph);
  }
  g.setLineDash([]);

  // 总尺寸 — 真实比例
  drawDimensionLine(g, { x: ox, y: oy - 14 }, { x: ox + finW, y: oy - 14 }, `${Math.round(uSpan)}`, 'horizontal');
  drawDimensionLine(g, { x: ox + finW + 16, y: oy }, { x: ox + finW + 16, y: oy + finH }, `${Math.round(vSpan)}`, 'vertical');

  // 内部 U 段尺寸（底）
  const uBoundaries = collectBoundaries(drawNodes, uAxis);
  if (uBoundaries.length > 2 && uBoundaries.length <= 18) {
    const yLine = oy + finH + 6;
    g.strokeStyle = '#5c4a38';
    g.lineWidth = 0.4;
    g.fillStyle = '#1a1612';
    g.font = '13px "Geist Mono", monospace';
    g.textAlign = 'center';
    g.textBaseline = 'top';
    for (let i = 0; i < uBoundaries.length - 1; i++) {
      const a = uBoundaries[i], b = uBoundaries[i + 1];
      const w = Math.round(b - a);
      if (w < 5) continue;
      const xa = ox + (a - uMin) * scaleFit;
      const xb = ox + (b - uMin) * scaleFit;
      g.beginPath();
      g.moveTo(xa, yLine); g.lineTo(xb, yLine);
      g.moveTo(xa, yLine - 3); g.lineTo(xa, yLine + 3);
      g.moveTo(xb, yLine - 3); g.lineTo(xb, yLine + 3);
      g.stroke();
      g.fillText(String(w), (xa + xb) / 2, yLine + 4);
    }
  }

  // 内部 V 段尺寸（左）
  const vBoundaries = collectBoundaries(drawNodes, vAxis);
  if (vBoundaries.length > 2 && vBoundaries.length <= 18) {
    const xLine = ox - 8;
    g.strokeStyle = '#5c4a38';
    g.lineWidth = 0.4;
    g.fillStyle = '#1a1612';
    g.font = '13px "Geist Mono", monospace';
    for (let i = 0; i < vBoundaries.length - 1; i++) {
      const a = vBoundaries[i], b = vBoundaries[i + 1];
      const h = Math.round(b - a);
      if (h < 5) continue;
      const ya = oy + (vMax - a) * scaleFit;
      const yb = oy + (vMax - b) * scaleFit;
      g.beginPath();
      g.moveTo(xLine, ya); g.lineTo(xLine, yb);
      g.moveTo(xLine - 2, ya); g.lineTo(xLine + 2, ya);
      g.moveTo(xLine - 2, yb); g.lineTo(xLine + 2, yb);
      g.stroke();
      g.save();
      g.translate(xLine - 3, (ya + yb) / 2);
      g.rotate(-Math.PI / 2);
      g.textAlign = 'center';
      g.textBaseline = 'bottom';
      g.fillText(String(h), 0, 0);
      g.restore();
    }
  }

}

/** 按面 ID 返回 4 视图的轴映射 */
function getSideAxes(sideId: SideId): {
  longEdge: 'x' | 'y' | 'z';
  shortEdge: 'x' | 'y' | 'z';
  thickness: 'x' | 'y' | 'z';
  innerLabel: string;
  outerLabel: string;
} {
  // 与 local-clone 一致的视图标签：底座/顶盖用"顶部/底部"，侧面/端面用"内侧/外侧"
  switch (sideId) {
    case 'pedestal':
    case 'topCap':
      return { longEdge: 'x', shortEdge: 'z', thickness: 'y', innerLabel: '顶部', outerLabel: '底部' };
    case 'sideFront':
    case 'sideBack':
    case 'endLeft':
    case 'endRight':
      return {
        longEdge: sideId === 'endLeft' || sideId === 'endRight' ? 'z' : 'x',
        shortEdge: 'y',
        thickness: sideId === 'endLeft' || sideId === 'endRight' ? 'x' : 'z',
        innerLabel: '内侧',
        outerLabel: '外侧',
      };
  }
}

/**
 * 计算指定页（2-5）的视图比例与失真信息。
 * 公式与 drawSidePlanView 完全一致，结果用于在标题栏 / 说明栏标注比例。
 * 详见 DRAWING-DESIGN.md §8.2 第 1 层
 */
export interface PageScaleInfo {
  /** 水平方向真实比例（1:N 中的 N） */
  ratioH: number;
  /** 垂直方向（截面厚度方向）真实比例 */
  ratioV: number;
  /** 厚度方向放大倍数（scaleV / scaleH） */
  amplify: number;
  /** 是否非等比绘制（厚度被放大时为 true） */
  isDistorted: boolean;
}
function computePageScale(ctx: DrawingContext, pageNo: PageNo): PageScaleInfo | null {
  const part = PAGE_PARTS.find(p => p.pageNo === pageNo);
  if (!part || part.sideIds.length === 0) return null;
  const sideIds = part.sideIds as SideId[];
  const allNodes = ctx.renderNodes.filter(n => sideIds.includes(n.sideId));
  if (allNodes.length === 0) return null;

  // renderArea 与 renderPageToCanvas 中常量保持一致：{ w: W-120, h: H-420 }
  const W = A4_LANDSCAPE_PX.width;
  const H = A4_LANDSCAPE_PX.height;
  const areaW = W - 120;
  const areaH = H - 365;  // 与 renderPageToCanvas 的 renderArea.h 同步

  const axes = getSideAxes(sideIds[0]);
  const longSpan = axisSpan(allNodes, axes.longEdge);
  const thickSpan = axisSpan(allNodes, axes.thickness);

  // 与 drawSidePlanView 完全一致 — 同步引用 SUBVIEW_PAD
  const colGap = 22;
  const rowGap = 14;
  const colW = (areaW - colGap) / 2;
  const topRowH = Math.floor(areaH * 0.38);
  void rowGap;
  const innerColW = colW - SUBVIEW_PAD.left - SUBVIEW_PAD.right;
  const sectionInnerH = topRowH - SUBVIEW_PAD.top - SUBVIEW_PAD.bottom;
  const THICKNESS_AMPLIFY = 8;  // 与 drawSidePlanView 保持一致
  const scaleH = Math.min(innerColW / longSpan, sectionInnerH / thickSpan / THICKNESS_AMPLIFY);
  const scaleV = scaleH * THICKNESS_AMPLIFY;

  const ratioH = Math.max(1, Math.round(1 / (scaleH * PX_TO_MM)));
  const ratioV = Math.max(1, Math.round(1 / (scaleV * PX_TO_MM)));
  const amplify = scaleV / scaleH;
  return {
    ratioH,
    ratioV,
    amplify,
    isDistorted: amplify > 1.2,
  };
}

/**
 * 把单个 RenderNode 画到 (px,py,pw,ph) 矩形内：
 *  - board + 有纹理 → drawImage 拉伸覆盖整块板（与 boxDesigner 3D 一致：每板一张完整木纹）
 *  - board + 无纹理 → 用 materialcolor 实色
 *  - 框架/横方     → 浅米色实色
 * 描边在外层调用方负责。
 */
function paintNode(
  g: CanvasRenderingContext2D,
  n: RenderNode,
  textures: Map<number, HTMLImageElement>,
  px: number,
  py: number,
  pw: number,
  ph: number,
): void {
  if (n.elementType === 'board') {
    const img = textures.get(n.material);
    if (img) {
      g.drawImage(img, px, py, pw, ph);
      return;
    }
    g.fillStyle = n.materialcolor || '#d9b87c';
    g.fillRect(px, py, pw, ph);
    return;
  }
  // 框架/横方
  g.fillStyle = '#f0e0c2';
  g.fillRect(px, py, pw, ph);
}

/** 预加载所有 nodes 涉及的木纹贴图 */
async function loadMaterialTextures(
  nodes: RenderNode[],
  library: Material[],
): Promise<Map<number, HTMLImageElement>> {
  const cache = new Map<number, HTMLImageElement>();
  const usedKeys = new Set(nodes.map(n => n.material));
  await Promise.all(
    Array.from(usedKeys).map(async (key) => {
      const mat = library.find(m => m.key === key);
      if (!mat?.textureUrl) return;
      try {
        const img = new Image();
        await new Promise<void>((res, rej) => {
          img.onload = () => res();
          img.onerror = () => rej(new Error('texture load failed'));
          img.src = mat.textureUrl!;
        });
        cache.set(key, img);
      } catch {
        // 单个贴图失败不阻塞其他
      }
    }),
  );
  return cache;
}

/** 收集 nodes 在某轴上的所有不同边界值（去重 + 排序） */
function collectBoundaries(nodes: RenderNode[], axis: 'x' | 'y' | 'z'): number[] {
  const set = new Set<number>();
  for (const n of nodes) {
    const half = n.geometry[AXIS_TO_GEO[axis]] / 2;
    set.add(Math.round((n.position[axis] - half) * 100) / 100);
    set.add(Math.round((n.position[axis] + half) * 100) / 100);
  }
  return Array.from(set).sort((a, b) => a - b);
}

function drawDimensionLine(
  g: CanvasRenderingContext2D,
  a: { x: number; y: number },
  b: { x: number; y: number },
  label: string,
  orientation: 'horizontal' | 'vertical',
): void {
  g.strokeStyle = '#5c4a38';
  g.lineWidth = 0.8;
  // 主线
  g.beginPath();
  g.moveTo(a.x, a.y);
  g.lineTo(b.x, b.y);
  g.stroke();
  // 短刻度
  if (orientation === 'horizontal') {
    g.beginPath();
    g.moveTo(a.x, a.y - 4); g.lineTo(a.x, a.y + 4);
    g.moveTo(b.x, b.y - 4); g.lineTo(b.x, b.y + 4);
    g.stroke();
    g.fillStyle = '#1a1612';
    g.font = '18px "Geist Mono", monospace';
    g.textAlign = 'center';
    g.textBaseline = 'bottom';
    g.fillText(label, (a.x + b.x) / 2, a.y - 4);
  } else {
    g.beginPath();
    g.moveTo(a.x - 4, a.y); g.lineTo(a.x + 4, a.y);
    g.moveTo(b.x - 4, b.y); g.lineTo(b.x + 4, b.y);
    g.stroke();
    g.fillStyle = '#1a1612';
    g.font = '18px "Geist Mono", monospace';
    g.save();
    g.translate(a.x + 14, (a.y + b.y) / 2);
    g.rotate(-Math.PI / 2);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(label, 0, 0);
    g.restore();
  }
}

const SIDE_LABEL: Record<SideId, string> = {
  pedestal: '底座',
  topCap: '顶盖',
  sideFront: '前侧',
  sideBack: '后侧',
  endLeft: '左端',
  endRight: '右端',
};

async function drawImageDataUrl(
  g: CanvasRenderingContext2D,
  dataUrl: string,
  area: { x: number; y: number; w: number; h: number },
): Promise<void> {
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('图片加载失败'));
    img.src = dataUrl;
  });
  // 保持比例填充
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  if (!iw || !ih) return;
  const r = Math.min(area.w / iw, area.h / ih);
  const dw = iw * r;
  const dh = ih * r;
  const dx = area.x + (area.w - dw) / 2;
  const dy = area.y + (area.h - dh) / 2;
  g.drawImage(img, dx, dy, dw, dh);
}

/** 渲染指定页集合并合并为一份 PDF 下载 */
export async function exportPagesToPdf(
  pageNos: PageNo[],
  ctx: DrawingContext,
  filename?: string,
): Promise<void> {
  if (pageNos.length === 0) {
    throw new Error('请至少选择一页');
  }

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pdfW = pdf.internal.pageSize.getWidth();
  const pdfH = pdf.internal.pageSize.getHeight();

  const offCanvas = document.createElement('canvas');

  for (let i = 0; i < pageNos.length; i++) {
    const pageNo = pageNos[i];
    await renderPageToCanvas(offCanvas, pageNo, ctx, pageNos.length);
    const jpeg = offCanvas.toDataURL('image/jpeg', 0.95);
    if (i > 0) pdf.addPage();
    pdf.addImage(jpeg, 'JPEG', 0, 0, pdfW, pdfH);
  }

  const safeName = filename || `${ctx.customerName || 'drawing'}_${ctx.orderNo || 'export'}`;
  pdf.save(`${sanitizeFilename(safeName)}.pdf`);
}

function sanitizeFilename(s: string): string {
  return s.replace(/[\\/:*?"<>|]/g, '_').slice(0, 80);
}

/**
 * 本图料单：生成 A4 横向 PDF，包含当前页对应面的 BOM 表。
 * 第一页（3D）→ 全部 BOM。行数超出单页可容纳时自动分页。
 */
export async function exportBomTablePdf(
  pageNo: PageNo,
  ctx: DrawingContext,
): Promise<void> {
  const part = PAGE_PARTS.find(p => p.pageNo === pageNo);
  if (!part) throw new Error('无效页号');

  // 第 1 页：全量 bom；第 2-5 页：按当前 sideIds 重新过滤聚合，避免跨面合并误算
  const sideIds = part.sideIds.length > 0 ? (part.sideIds as SideId[]) : null;
  const lines: MaterialTableRow[] = sideIds
    ? summarizePageBom(ctx.renderNodes, sideIds, ctx.materialLibrary)
    : ctx.bomLines;

  const canvas = document.createElement('canvas');
  canvas.width = A4_LANDSCAPE_PX.width;
  canvas.height = A4_LANDSCAPE_PX.height;
  const g = canvas.getContext('2d');
  if (!g) throw new Error('无法获取 2D context');
  const W = canvas.width, H = canvas.height;
  const tableArea = { x: 60, y: 180, w: W - 120, h: H - 235 };

  // 单页可容纳行数：减表头一行
  const ROW_H = 38;
  const rowsPerPage = Math.max(1, Math.floor((tableArea.h - ROW_H) / ROW_H));
  const totalPages = Math.max(1, Math.ceil(lines.length / rowsPerPage));

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pdfW = pdf.internal.pageSize.getWidth();
  const pdfH = pdf.internal.pageSize.getHeight();

  for (let p = 0; p < totalPages; p++) {
    const start = p * rowsPerPage;
    const end = Math.min(lines.length, start + rowsPerPage);
    const slice = lines.slice(start, end);

    // 清空画布
    g.fillStyle = '#ffffff';
    g.fillRect(0, 0, W, H);

    // 外框
    g.strokeStyle = '#1a1612';
    g.lineWidth = 2;
    g.strokeRect(40, 40, W - 80, H - 80);

    // 顶部标题
    g.fillStyle = '#1a1612';
    g.font = 'bold 36px "Microsoft YaHei", sans-serif';
    g.textAlign = 'center';
    g.fillText(`${ctx.companyName} · 本图料单`, W / 2, 80);
    g.font = '20px "Microsoft YaHei", sans-serif';
    g.fillStyle = '#5c4a38';
    const meta = totalPages > 1
      ? `订单 ${ctx.orderNo} · ${part.label} · 共 ${lines.length} 项 · 第 ${p + 1}/${totalPages} 页`
      : `订单 ${ctx.orderNo} · ${part.label} · 共 ${lines.length} 项`;
    g.fillText(meta, W / 2, 108);

    // 分割线
    g.strokeStyle = '#1a1612';
    g.lineWidth = 1;
    g.beginPath();
    g.moveTo(40, 130); g.lineTo(W - 40, 130);
    g.stroke();

    // 表（带连续序号 rowOffset = start）
    drawMaterialsTableFull(g, tableArea, slice, start);

    // 底部脚注
    g.fillStyle = '#5c4a38';
    g.font = '18px "Microsoft YaHei", sans-serif';
    g.textAlign = 'center';
    g.fillText('版权所有，未经允许不可使用图纸中的设计', W / 2, H - 25);

    if (p > 0) pdf.addPage();
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, pdfW, pdfH);
  }

  pdf.save(sanitizeFilename(`${ctx.customerName || 'drawing'}_${ctx.orderNo}_${part.label}_料单`) + '.pdf');
}

/** drawMaterialsTableFull 接受的最小行结构：BomLine 与 PageBomRow 都满足 */
type MaterialTableRow = {
  spec: string;
  materialName: string;
  count: number;
  totalVolumeM3: number;
  totalWeightKg: number;
  fromSides: SideId[];
};

/**
 * 完整版材料表（用于本图料单 PDF）。
 * 调用方负责切片 + 多页循环；rowOffset 让序号跨页连续（第 2 页起 = 31, 32, ...）。
 */
function drawMaterialsTableFull(
  g: CanvasRenderingContext2D,
  area: { x: number; y: number; w: number; h: number },
  rows: MaterialTableRow[],
  rowOffset: number,
): void {
  g.strokeStyle = '#1a1612';
  g.lineWidth = 1;
  g.strokeRect(area.x, area.y, area.w, area.h);

  const cols = [
    { label: '序号', w: 80 },
    { label: '部位', w: 220 },
    { label: '规格 (mm)', w: 320 },
    { label: '材料', w: 280 },
    { label: '数量', w: 120 },
    { label: '体积 m³', w: 160 },
    { label: '重量 kg', w: 160 },
  ];
  const totalColsW = cols.reduce((s, c) => s + c.w, 0);
  const scaleX = area.w / totalColsW;
  cols.forEach(c => { c.w = c.w * scaleX; });

  const rowH = 38;

  // 表头
  let cx = area.x;
  g.fillStyle = '#f4ede0';
  g.fillRect(area.x, area.y, area.w, rowH);
  g.strokeStyle = '#1a1612';
  g.font = 'bold 14px "Microsoft YaHei", sans-serif';
  g.textBaseline = 'middle';
  g.textAlign = 'center';
  for (const col of cols) {
    g.strokeRect(cx, area.y, col.w, rowH);
    g.fillStyle = '#1a1612';
    g.fillText(col.label, cx + col.w / 2, area.y + rowH / 2);
    cx += col.w;
  }

  // 行
  g.font = '18px "Geist Mono", monospace';
  for (let i = 0; i < rows.length; i++) {
    const line = rows[i];
    const rowY = area.y + (i + 1) * rowH;
    cx = area.x;
    for (const col of cols) {
      g.strokeRect(cx, rowY, col.w, rowH);
      cx += col.w;
    }
    const partLabel = line.fromSides.map(s => SIDE_LABEL[s] || s).join('/');
    const cells: string[] = [
      String(rowOffset + i + 1),
      partLabel,
      line.spec,
      line.materialName,
      String(line.count),
      line.totalVolumeM3.toFixed(4),
      line.totalWeightKg.toFixed(2),
    ];
    cx = area.x;
    g.fillStyle = '#1a1612';
    for (let c = 0; c < cols.length; c++) {
      g.textAlign = 'center';
      g.fillText(cells[c], cx + cols[c].w / 2, rowY + rowH / 2);
      cx += cols[c].w;
    }
  }

  if (rows.length === 0) {
    g.fillStyle = '#8a7456';
    g.textAlign = 'center';
    g.font = '20px "Microsoft YaHei", sans-serif';
    g.fillText('暂无材料数据', area.x + area.w / 2, area.y + area.h / 2);
  }
}
